import { clsx } from "clsx";
import { motion } from "framer-motion";
import type { InputHTMLAttributes, ReactNode } from "react";
import { CheckIcon } from "../icons/CheckIcon";

type CheckboxProps = {
  label: ReactNode;
  description?: ReactNode;
  badge?: ReactNode;
  className?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "className">;

export const Checkbox = ({
  label,
  description,
  badge,
  className,
  checked,
  disabled,
  ...props
}: CheckboxProps) => {
  return (
    <label
      className={clsx(
        "flex cursor-pointer items-start gap-3 rounded-xl p-3 transition-colors duration-150",
        checked ? "bg-ocean-50" : "hover:bg-ocean-50/50",
        disabled && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      <div className="relative shrink-0 pt-0.5">
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          className="peer sr-only"
          {...props}
        />
        <motion.div
          initial={false}
          animate={{
            backgroundColor: checked ? "#2563a3" : "#ffffff",
            borderColor: checked ? "#2563a3" : "#b3d4f0",
          }}
          transition={{ duration: 0.15 }}
          className={clsx(
            "flex h-6 w-6 items-center justify-center rounded-md border-2 transition-shadow",
            "peer-focus-visible:shadow-focus",
          )}
        >
          <motion.span
            initial={{ scale: 0, opacity: 0 }}
            animate={{
              scale: checked ? 1 : 0,
              opacity: checked ? 1 : 0,
            }}
            transition={{ type: "spring", stiffness: 500, damping: 30 }}
            className="text-white"
          >
            <CheckIcon />
          </motion.span>
        </motion.div>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-ocean-900">{label}</span>
          {badge}
        </div>
        {description && <div className="mt-0.5 text-ocean-500 text-sm">{description}</div>}
      </div>
    </label>
  );
};
